import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProfile, uploadProfilePhoto, uploadCoverPhoto } from '../../store/slices/profileSlice';
import { fetchLoveShots } from '../../store/slices/loveshotsSlice';
import { fetchStories } from '../../store/slices/storiesSlice';
import { followUser, unfollowUser, checkFollowing } from '../../store/slices/followSlice';
import { createConversation } from '../../store/slices/messagesSlice';
import LoveShotCard from '../../components/features/loveshots/LoveShotCard';
import { FaHeart, FaMapMarkerAlt, FaCalendarAlt, FaCamera, FaEdit, FaSpinner, FaUserPlus, FaUserCheck, FaHistory, FaUpload, FaImage, FaCertificate, FaRocket } from 'react-icons/fa';

import { toast } from 'react-toastify';
import './Profile.css';

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { currentProfile, loading: profileLoading, error, errorStatus } = useSelector((state) => state.profile);
  const { items: loveshots, loading: loveshotsLoading } = useSelector((state) => state.loveshots);
  const { items: stories, loading: storiesLoading } = useSelector((state) => state.stories);

  const { followingStatus } = useSelector((state) => state.follow);
  const { user: currentUser } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('loveshots');
  const [isFollowing, setIsFollowing] = useState(false);

  const photoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const isOwnProfile = currentUser?.id === userId;

  const handleEditProfile = () => {
    navigate('/settings');
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await dispatch(uploadProfilePhoto({ profileId: userId, photoFile: file }));
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Photo de profil mise à jour avec succès !');
      } else {
        toast.error('Erreur lors de la mise à jour de la photo de profil');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload de la photo');
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await dispatch(uploadCoverPhoto({ profileId: userId, coverFile: file }));
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Photo de couverture mise à jour avec succès !');
      } else {
        toast.error('Erreur lors de la mise à jour de la photo de couverture');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload de la photo de couverture');
    }
  };

  const triggerPhotoUpload = () => {
    photoInputRef.current?.click();
  };

  const triggerCoverUpload = () => {
    coverInputRef.current?.click();
  };

  const handleFollow = async () => {
    try {
      if (isFollowing) {
        await dispatch(unfollowUser(userId)).unwrap();
        setIsFollowing(false);
        toast.success('Vous ne suivez plus cet utilisateur');
      } else {
        await dispatch(followUser(userId)).unwrap();
        setIsFollowing(true);
        toast.success('Vous suivez maintenant cet utilisateur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'action de suivi');
    }
  };

  const handleMessage = async () => {
    try {
      const result = await dispatch(createConversation(userId)).unwrap();
      navigate('/messages');
    } catch (error) {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchProfile(userId));
      // Fetch user's loveshots
      dispatch(fetchLoveShots({ page: 1, userId }));
      // Fetch stories for this user
      dispatch(fetchStories({ user_id: userId }));
      // Check if following this user
      if (!isOwnProfile) {
        dispatch(checkFollowing(userId));
      }
    }
  }, [dispatch, userId, isOwnProfile]);


  useEffect(() => {
    if (followingStatus[userId] !== undefined) {
      setIsFollowing(followingStatus[userId]);
    }
  }, [followingStatus, userId]);

  if (profileLoading) {
    return (
      <div className="profile-loading">
        <FaSpinner className="spinner" />
        <p>Chargement du profil...</p>
      </div>
    );
  }

  if (errorStatus) {
    if (errorStatus === 404) {
      return (
        <div className="profile-error">
          <h2>Profil non trouvé</h2>
          <p>Ce profil n'existe pas.</p>
        </div>
      );
    } else if (errorStatus === 403) {
      return (
        <div className="profile-error">
          <h2>Profil privé</h2>
          <p>Ce profil est privé.</p>
        </div>
      );
    } else {
      return (
        <div className="profile-error">
          <h2>Erreur</h2>
          <p>{error?.message || 'Une erreur est survenue.'}</p>
        </div>
      );
    }
  }

  if (!currentProfile) {
    return (
      <div className="profile-error">
        <h2>Profil non trouvé</h2>
        <p>Ce profil n'existe pas.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={photoInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={coverInputRef}
        onChange={handleCoverUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-cover">
          {currentProfile.cover_photo && (
            <img src={currentProfile.cover_photo} alt="Cover" className="cover-image" />
          )}
          {isOwnProfile && (
            <button className="upload-cover-btn" onClick={triggerCoverUpload} title="Changer la photo de couverture">
              <FaImage />
            </button>
          )}
        </div>

        <div className="profile-info">
          <div className="profile-avatar-section">
            <img
              src={currentProfile.photo_url || '/default-avatar.svg'}

              alt={currentProfile.user?.username}
              className="profile-avatar"
            />
            {isOwnProfile && (
              <>
                <button className="upload-photo-btn" onClick={triggerPhotoUpload} title="Changer la photo de profil">
                  <FaCamera />
                </button>
                <button className="edit-profile-btn" onClick={handleEditProfile}>
                  <FaEdit />
                </button>
              </>
            )}
          </div>

          <div className="profile-details">
            <h1 className="profile-name">
              {currentProfile.first_name} {currentProfile.last_name}
            </h1>
            <p className="profile-username">@{currentProfile.user?.username}</p>

            <div className="profile-stats">
              <div className="stat">
                <span className="stat-number">{currentProfile.loveshots_count || 0}</span>
                <span className="stat-label">LoveShots</span>
              </div>
              <div className="stat">
                <span className="stat-number">{currentProfile.followers_count || 0}</span>
                <span className="stat-label">Followers</span>
              </div>
              <div className="stat">
                <span className="stat-number">{currentProfile.following_count || 0}</span>
                <span className="stat-label">Following</span>
              </div>
            </div>

            <div className="profile-bio">
              {currentProfile.bio && <p>{currentProfile.bio}</p>}

              <div className="profile-meta">
                {currentProfile.location && (
                  <span className="meta-item">
                    <FaMapMarkerAlt /> {currentProfile.location}
                  </span>
                )}
                {currentProfile.date_of_birth && (
                  <span className="meta-item">
                    <FaCalendarAlt /> {new Date(currentProfile.date_of_birth).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {currentProfile.interests && (
                  <div className="interests">
                    {currentProfile.interests.split(',').map((interest, index) => (
                      <span key={index} className="interest-tag">
                        {interest.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!isOwnProfile && (
              <div className="profile-actions">
                <button className="btn-follow" onClick={handleFollow}>
                  {isFollowing ? <FaUserCheck /> : <FaUserPlus />} {isFollowing ? 'Suivi' : 'Suivre'}
                </button>
                <button className="btn-message" onClick={handleMessage}>
                  💬 Message
                </button>
              </div>
            )}

            {isOwnProfile && (
              <div className="profile-actions mt-3">
                <button 
                  className="btn-certification" 
                  onClick={() => navigate('/settings/certification')}
                  title="Certifier mon compte"
                >
                  <FaCertificate /> Certification
                </button>
                <button 
                  className="btn-boost" 
                  onClick={() => navigate('/boosts')}
                  title="Booster mon profil"
                >
                  <FaRocket /> Boost
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Profile Content Tabs */}
      <div className="profile-content">
        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'loveshots' ? 'active' : ''}`}
            onClick={() => setActiveTab('loveshots')}
          >
            <FaCamera /> LoveShots
          </button>
          <button
            className={`tab-btn ${activeTab === 'stories' ? 'active' : ''}`}
            onClick={() => setActiveTab('stories')}
          >
            <FaHistory /> Stories
          </button>
          <button
            className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            <FaHeart /> À propos
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'loveshots' && (
            <div className="loveshots-tab">
              {loveshotsLoading ? (
                <div className="loading-container">
                  <FaSpinner className="spinner" />
                  <p>Chargement des LoveShots...</p>
                </div>
              ) : loveshots.length > 0 ? (
                <div className="user-loveshots">
                  {loveshots.map((loveshot) => (
                    <LoveShotCard key={loveshot.id} loveshot={loveshot} />
                  ))}
                </div>
              ) : (
                <div className="empty-loveshots">
                  <FaCamera className="empty-icon" />
                  <h3>Aucun LoveShot</h3>
                  <p>{isOwnProfile ? 'Partagez votre premier LoveShot !' : 'Cet utilisateur n\'a pas encore partagé de LoveShots.'}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stories' && (
            <div className="stories-tab">
              {storiesLoading ? (
                <div className="loading-container">
                  <FaSpinner className="spinner" />
                  <p>Chargement des Stories...</p>
                </div>
              ) : stories && stories.filter(story => story.user_id === userId).length > 0 ? (
                <div className="stories-grid">
                  {stories.filter(story => story.user_id === userId).map((story) => (
                    <div key={story.id} className="story-card">
                      <div className="story-user">
                        <img 
                          src={currentProfile.photo_url || '/default-avatar.svg'} 
                          alt={currentProfile.user?.username} 
                        />
                        <span>{currentProfile.user?.username}</span>
                      </div>
                      {story.media_file && story.media_type === 'photo' && (
                        <img src={story.media_file} alt="Story" className="story-image" />
                      )}
                      {story.media_file && story.media_type === 'video' && (
                        <video src={story.media_file} className="story-image" controls />
                      )}
                      {story.caption && <p className="story-content">{story.caption}</p>}
                      <div className="story-meta">
                        <span>{new Date(story.created_at).toLocaleString('fr-FR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-stories">
                  <FaHistory className="empty-icon" />
                  <h3>Aucune Story</h3>
                  <p>{isOwnProfile ? 'Partagez votre première Story !' : 'Cet utilisateur n\'a pas encore partagé de Stories.'}</p>
                </div>
              )}
            </div>
          )}


          {activeTab === 'about' && (
            <div className="about-tab">
              <div className="about-section">
                <h3>Informations personnelles</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Genre:</label>
                    <span>{currentProfile.gender === 'M' ? 'Homme' : currentProfile.gender === 'F' ? 'Femme' : 'Non spécifié'}</span>
                  </div>
                  {currentProfile.relationship_status && (
                    <div className="info-item">
                      <label>Statut relationnel:</label>
                      <span>{currentProfile.relationship_status}</span>
                    </div>
                  )}
                  {currentProfile.occupation && (
                    <div className="info-item">
                      <label>Profession:</label>
                      <span>{currentProfile.occupation}</span>
                    </div>
                  )}
                  {currentProfile.education && (
                    <div className="info-item">
                      <label>Éducation:</label>
                      <span>{currentProfile.education}</span>
                    </div>
                  )}
                </div>
              </div>

              {currentProfile.certifications && currentProfile.certifications.length > 0 && (
                <div className="about-section">
                  <h3>Certifications</h3>
                  <div className="certifications-list">
                    {currentProfile.certifications.map((cert, index) => (
                      <div key={index} className="certification-item">
                        <span className="cert-name">{cert.name}</span>
                        <span className="cert-issuer">{cert.issuer}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
